/**
 * Source of the token-NAME part of a `{{token}}`: a bare name (no braces, no
 * whitespace, no `|`) optionally followed by a date-format tail `|D MMM YYYY`,
 * which is the one place a token legitimately contains spaces. Helper args
 * (`{{x fallback="y"}}`) are still excluded by the whitespace rule. Shared with
 * the pill parser so what renders as a pill is exactly what resolves as a token.
 */
export const TOKEN_NAME_PATTERN = "[^{}\\s|]+(?:\\|[^{}]*?)?";

const TOKEN_RE = new RegExp(
  `\\{\\{\\{?\\s*(${TOKEN_NAME_PATTERN})\\s*\\}?\\}\\}`,
  "g",
);

// Find the actual key in an object that matches `key`, preferring an exact
// match and otherwise falling back to a case-insensitive one (e.g. token
// "firstname" vs data "firstName"). Returns undefined when nothing matches.
const findExistingKey = (
  obj: Record<string, unknown>,
  key: string,
): string | undefined => {
  // Own keys only: `key in` also matched Object.prototype, so a deck reading
  // `{{constructor}}` printed `function Object() { [native code] }`.
  if (Object.prototype.hasOwnProperty.call(obj, key)) return key;
  const lower = key.toLowerCase();
  return Object.keys(obj).find((k) => k.toLowerCase() === lower);
};

// Look up a single key in an object, with case-insensitive fallback.
const getKey = (obj: Record<string, unknown>, key: string): unknown => {
  const matchKey = findExistingKey(obj, key);
  return matchKey === undefined ? undefined : obj[matchKey];
};

/**
 * Resolve a variable name against version data, lodash-`get`-style.
 *
 * Dot-notation names (e.g. "hubspot.contact.firstName") always resolve against
 * the NESTED object (`data.hubspot.contact.firstName`). The data may ALSO carry
 * a flat duplicate key (e.g. `data["hubspot.contact.firstName"]`, often an empty
 * string) — that inline key is intentionally ignored so a stale/empty duplicate
 * never shadows the real nested value. Names without a "." are genuine
 * top-level keys and are read directly. Each path segment is matched
 * case-insensitively when no exact match exists, so casing differences between
 * the token and the data keys don't block resolution. When a segment lands on
 * an array the walk continues from element 0 (repeatable groups store rows as
 * arrays; the editor shows row 1).
 */
export const getVariableValue = (
  data: Record<string, unknown> | undefined | null,
  name: string,
): unknown => {
  if (!data || !name) return undefined;

  if (name.indexOf(".") === -1) {
    return getKey(data as Record<string, unknown>, name);
  }

  let current: unknown = data;
  for (const segment of name.split(".")) {
    if (current == null || typeof current !== "object") return undefined;
    // Repeatable rows: a path that crosses an array reads ROW 1. The editor
    // shows one authored instance per repeatable block; the share dialog,
    // the bake and the runtime keep their own per-row loops.
    if (Array.isArray(current)) current = current[0];
    if (current == null || typeof current !== "object") return undefined;
    current = getKey(current as Record<string, unknown>, segment);
  }
  return current;
};

/**
 * Write a value into version data under a variable name, mirroring how
 * {@link getVariableValue} reads it. Dot-notation names are written into the
 * NESTED object (`data.hubspot.contact.firstName`), creating intermediate
 * objects as needed and reusing any existing key (case-insensitively) so the
 * real nested value is updated in place rather than a duplicate being created.
 * Names without a "." are written as top-level keys. Mutates `data` directly
 * (safe inside an Immer reducer draft).
 */
export const setVariableValue = (
  data: Record<string, unknown>,
  name: string,
  value: unknown,
): void => {
  if (!data || !name) return;

  if (name.indexOf(".") === -1) {
    data[findExistingKey(data, name) ?? name] = value;
    return;
  }

  const segments = name.split(".");
  let current: Record<string, unknown> = data;
  for (let i = 0; i < segments.length - 1; i++) {
    const key = findExistingKey(current, segments[i]) ?? segments[i];
    const next = current[key];
    if (next == null || typeof next !== "object") {
      current[key] = {};
    }
    current = current[key] as Record<string, unknown>;
  }

  const last = segments[segments.length - 1];
  current[findExistingKey(current, last) ?? last] = value;
};

/**
 * Stringify a resolved variable value for display in text.
 *
 * A multiselect variable's value is an ARRAY of the selected values, and the
 * default `String(array)` joins with a bare comma ("Value #2,Value #3"). Render
 * it as a readable list instead ("Value #2, Value #3" — matches how the share
 * dialog's own multiselect control summarises the selection). Empty/nullish
 * entries are dropped so a partially filled selection can't emit a stray ", ".
 */
export const stringifyVariableValue = (value: unknown): string => {
  if (value == null) return "";
  if (Array.isArray(value)) {
    return value
      .filter((item) => item != null && item !== "")
      .map((item) => String(item))
      .join(", ");
  }
  return String(value);
};

export const resolveTokens = (
  template: string | undefined | null,
  data: Record<string, unknown> | undefined | null,
): string => {
  if (!template) return "";
  if (!data) return template.replace(TOKEN_RE, "");
  return template.replace(TOKEN_RE, (_, name) =>
    stringifyVariableValue(getVariableValue(data, name)),
  );
};

export const parseTokens = (
  template: string | undefined | null,
): Array<{ type: "text"; value: string } | { type: "var"; name: string }> => {
  if (!template) return [];
  const segments: Array<
    { type: "text"; value: string } | { type: "var"; name: string }
  > = [];
  let lastIndex = 0;
  TOKEN_RE.lastIndex = 0;
  let match: RegExpExecArray | null = TOKEN_RE.exec(template);
  while (match !== null) {
    if (match.index > lastIndex) {
      segments.push({
        type: "text",
        value: template.slice(lastIndex, match.index),
      });
    }
    segments.push({ type: "var", name: match[1] });
    lastIndex = match.index + match[0].length;
    match = TOKEN_RE.exec(template);
  }
  if (lastIndex < template.length) {
    segments.push({ type: "text", value: template.slice(lastIndex) });
  }
  return segments;
};

export const hasTokens = (template: string | undefined | null): boolean => {
  if (!template) return false;
  TOKEN_RE.lastIndex = 0;
  return TOKEN_RE.test(template);
};

const SOLE_TOKEN_RE = new RegExp(
  `^\\{\\{\\{?\\s*(${TOKEN_NAME_PATTERN})\\s*\\}?\\}\\}$`,
);

/**
 * Returns the variable name when the entire string is a single {{var}} token,
 * otherwise null. Use to detect a "fully variable-bound" field whose value
 * lives in version.data rather than in the template itself.
 */
export const getSoleTokenName = (
  template: string | undefined | null,
): string | null => {
  if (!template) return null;
  const match = template.match(SOLE_TOKEN_RE);
  return match ? match[1] : null;
};

const findChangeRange = (oldStr: string, newStr: string) => {
  let prefixLen = 0;
  while (
    prefixLen < oldStr.length &&
    prefixLen < newStr.length &&
    oldStr[prefixLen] === newStr[prefixLen]
  )
    prefixLen++;

  let oldEnd = oldStr.length;
  let newEnd = newStr.length;
  while (
    oldEnd > prefixLen &&
    newEnd > prefixLen &&
    oldStr[oldEnd - 1] === newStr[newEnd - 1]
  ) {
    oldEnd--;
    newEnd--;
  }

  return {
    deleteStart: prefixLen,
    deleteEnd: oldEnd,
    insertText: newStr.slice(prefixLen, newEnd),
  };
};

/**
 * Reconcile an edit applied to the resolved view of a template back into the
 * template itself. Variables whose resolved text is untouched are preserved as
 * tokens; variables whose resolved text is edited become literal text.
 */
export const reconcileTemplate = (
  oldTemplate: string,
  newResolved: string,
  data: Record<string, unknown> | undefined | null,
): string => {
  const oldResolved = resolveTokens(oldTemplate, data);
  if (oldResolved === newResolved) return oldTemplate;

  const { deleteStart, deleteEnd, insertText } = findChangeRange(
    oldResolved,
    newResolved,
  );

  const resolveOne = (name: string): string => {
    const v = getVariableValue(data, name);
    return v == null ? "" : String(v);
  };

  let pos = 0;
  const segments = parseTokens(oldTemplate).map((seg) => {
    const text = seg.type === "text" ? seg.value : resolveOne(seg.name);
    const start = pos;
    pos += text.length;
    return { ...seg, resolvedText: text, start, end: pos };
  });

  type InsertPlacement =
    | { kind: "claim"; idx: number }
    | { kind: "after"; idx: number }
    | { kind: "before"; idx: number }
    | { kind: "append" };

  const placement: InsertPlacement = (() => {
    if (deleteStart < deleteEnd) {
      for (let idx = 0; idx < segments.length; idx++) {
        const seg = segments[idx];
        const overlap =
          Math.min(seg.end, deleteEnd) - Math.max(seg.start, deleteStart);
        if (overlap > 0) return { kind: "claim", idx };
      }
      return { kind: "append" };
    }
    const p = deleteStart;
    for (let i = segments.length - 1; i >= 0; i--) {
      if (segments[i].end === p && segments[i].type === "text")
        return { kind: "claim", idx: i };
    }
    for (let idx = 0; idx < segments.length; idx++) {
      const seg = segments[idx];
      if (seg.start === p && seg.type === "text") return { kind: "claim", idx };
    }
    for (let idx = 0; idx < segments.length; idx++) {
      const seg = segments[idx];
      if (seg.start < p && p < seg.end) return { kind: "claim", idx };
    }
    for (let i = segments.length - 1; i >= 0; i--) {
      if (segments[i].end === p) return { kind: "after", idx: i };
    }
    if (segments.length > 0) return { kind: "before", idx: 0 };
    return { kind: "append" };
  })();

  let result = "";
  for (let idx = 0; idx < segments.length; idx++) {
    const seg = segments[idx];
    if (placement.kind === "before" && placement.idx === idx) {
      result += insertText;
    }

    const overlapStart = Math.max(seg.start, deleteStart);
    const overlapEnd = Math.min(seg.end, deleteEnd);
    const hasOverlap = overlapStart < overlapEnd;
    const claimsInsert = placement.kind === "claim" && placement.idx === idx;

    if (!hasOverlap && !claimsInsert) {
      result += seg.type === "var" ? `{{${seg.name}}}` : seg.value;
    } else {
      const segText = seg.resolvedText;
      let newText: string;
      if (hasOverlap) {
        const localDelStart = Math.max(0, deleteStart - seg.start);
        const localDelEnd = Math.min(segText.length, deleteEnd - seg.start);
        newText =
          segText.slice(0, localDelStart) +
          (claimsInsert ? insertText : "") +
          segText.slice(localDelEnd);
      } else {
        const local = deleteStart - seg.start;
        newText = segText.slice(0, local) + insertText + segText.slice(local);
      }
      result += newText;
    }

    if (placement.kind === "after" && placement.idx === idx) {
      result += insertText;
    }
  }

  if (placement.kind === "append") result += insertText;

  return result;
};
